import { Button } from 'components/universal/button';
import { Controller } from 'react-hook-form';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import cls from './editWorkplaceData.module.scss';
import cities from '../../../utils/cities.json';
import { DropdownIndicator } from '../addMultipleUser';
import { Input } from '../input';
import { Workplace } from 'views/employee-management/useEmployees';
import { DepartmentOption, useEditWorkplace } from './useEditWorkplaceData';
import { createDepartment, fetchDepartments } from 'services/registration';
import { AsyncPaginate } from 'react-select-async-paginate';

interface EditWorkplaceDataProps {
    workplaceId: number;
    adminWorkplaces: Workplace[];
    onCancel: (reload?: boolean) => void;
}

export const EditWorkplaceData = ({ workplaceId, adminWorkplaces, onCancel }: EditWorkplaceDataProps) => {
    const { t } = useTranslation();

    const {
        register,
        handleSubmit,
        errors,
        control,
        onSubmit,
        formatZipCode,
        onChangeCity,
        workplaceName,
        newDepartmentName, 
        setNewDepartmentName, 
        creating, 
        setCreating,
        newDepartmentInputRef,
        asyncSelectRef,
        selectedDepartment, 
        setSelectedDepartment, 
        departmentOptions, 
        setDepartmentOptions,
        setValue
    } = useEditWorkplace({ workplaceId, adminWorkplaces, onCancel });

    return (
        <>
            <h3 className={cls.modalHeader}>{t('registration.editWorkplaceLabel')}</h3>
            <p className={cls.modalDescription}>{workplaceName}</p>

            <form className={cls.form} onSubmit={handleSubmit(onSubmit)} noValidate>
                <input {...register('id')} type="hidden" />
                <Input
                    className={`${cls.inputFullWidth} mb-6`}
                    register={register}
                    id="workplace_name"
                    name="workplace_name"
                    label={t('registration.workplaceName')}
                    error={t(errors.workplace_name?.message)}
                    maxLength={255}
                />

                <div className={cls.selectDepartment}>
                    <div className={cls.labelWrapper}>
                        <label htmlFor="department" className={cls.label}>
                            {t('registration.department')}
                        </label>
                        
                        {!creating ? (
                            <a onClick={() => setCreating(true)} className={cls.addNewDepartmentButton}>
                                {t('registration.addDepartment')}
                            </a>
                        ) : 

                            <>
                                <a 
                                    className={cls.addNewDepartmentButton}
                                    onClick={async () => {
                                        const res = await createDepartment(workplaceId, newDepartmentName);
                                        const newOption = { label: res.name, value: res.id };
                                        setDepartmentOptions(prev => [...prev, newOption]);
                                        setSelectedDepartment(newOption);
                                        setCreating(false);
                                        setNewDepartmentName('');
                                        setValue('department', newOption);
                                    }}
                                    >
                                    {t('registration.saveDepartment')}
                                </a>
                                <a onClick={() => setCreating(false)} className={cls.cancelButton}>
                                    {t('registration.cancel')}
                                </a>
                            </>
                        }
                    </div>

                    {creating ? (
                        <div className={cls.newDepartmentWrapper}>
                            <div className={cls.fakeInputWrapper}>
                                <input
                                    ref={newDepartmentInputRef}
                                    className={`${cls.inputFullWidth} ${cls.fakeInput}`}
                                    id='new_department'
                                    type="text"
                                    value={newDepartmentName}
                                    placeholder={t('registration.department')}
                                    onChange={(e) => setNewDepartmentName(e.target.value)}
                                />
                            </div>
                        </div>
                    ) : (
                        <Controller
                        name="department"
                        control={control}
                        render={({ field }) => {
                            const currentValue: DepartmentOption | null =
                            field.value && typeof field.value === 'object' ? field.value : null;

                            return (
                                <AsyncPaginate
                                    selectRef={asyncSelectRef}
                                    value={selectedDepartment}
                                    options={departmentOptions}
                                    onChange={(option) => {
                                        setSelectedDepartment(option);
                                        setValue('department', option);
                                    }}
                                    loadOptions={async (inputValue, loadedOptions, { page }) => {
                                        const res = await fetchDepartments(workplaceId, page);
                                        const options = res.data.map(d => ({ label: d.name, value: d.id }));
                                        setDepartmentOptions(prev => [...prev, ...options.filter(o => !prev.find(p => p.value === o.value))]);
                                        return {
                                            options,
                                            hasMore: res.current_page < res.last_page,
                                            additional: { page: (page || 1) + 1 },
                                        };
                                    }}
                                    className={errors.workplace_name ? 'error react-select-container' : 'react-select-container'}
                                    getOptionLabel={(option) => option.label}
                                    getOptionValue={(option) => String(option.value)}
                                    additional={{ page: 1 }}
                                    classNamePrefix="react-select"
                                    placeholder=""
                                    components={{ DropdownIndicator }}
                                    debounceTimeout={300}
                                />
                            );
                        }}
                        />
                    )}
                </div>

                <Input
                    className={`${cls.input} mb-6`}
                    register={register}
                    id="workplace_address_postcode"
                    name="workplace_address_postcode"
                    label={t('registration.zipCode')}
                    error={t(errors.workplace_address_postcode?.message)}
                    onKeyUp={(e) => formatZipCode(e as any)}
                    maxLength={6}
                />

                <div className={cls.select}>
                    <label htmlFor="workplace_address_city" className={cls.label}>
                        {t('registration.city')}
                    </label>
                    <Controller
                        name="workplace_address_city"
                        control={control}
                        render={({ field }) => (
                            <Select
                                {...field}
                                options={cities}
                                className={errors.workplace_address_city ? 'error react-select-container' : 'react-select-container'}
                                classNamePrefix="react-select"
                                placeholder=""
                                components={{ DropdownIndicator }}
                                onChange={(selected) => onChangeCity(selected)}
                                value={cities.find(c => c.value === field.value) || null}
                                noOptionsMessage={() => t('registration.noOptions')}
                            />
                        )}
                    />
                </div>

                <Input
                    className={`${cls.input} mb-6`}
                    register={register}
                    id="workplace_address_street"
                    name="workplace_address_street"
                    label={t('registration.street')}
                    error={t(errors.workplace_address_street?.message)}
                    maxLength={255}
                />
                <Input
                    className={`${cls.input} mb-6`}
                    register={register}
                    id="workplace_address_number"
                    name="workplace_address_number"
                    label={t('registration.apartmentNumber')}
                    error={t(errors.workplace_address_number?.message)}
                    maxLength={255}
                />

                <div className={cls.modalButtonWrapper}>
                    <Button className={cls.modalButton} styleType="border" type="button" onClick={() => onCancel()}>
                        {t('employeeManagement.cancel')}
                    </Button>
                    <Button className={cls.modalButton} type="submit">
                        {t('employeeManagement.send2')}
                    </Button>
                </div>
            </form>
        </>
    );
};